import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { Card } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { RainbowButton } from "./ui/rainbow-button";

interface Analysis {
  id: number;
  name: string;
  status: string;
  uploaded_at: string;
  analysis_version_id: number;
  company_name: string;
}

interface ApiResponse {
  pitch_decks: Analysis[];
  total_count: number;
  page: number;
  limit: number;
}

export const AnalysisHistory: React.FC = () => {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 5;
  
  const { api } = useAuth();
  const navigate = useNavigate();

  const fetchAnalyses = async (pageNum: number = 1, append: boolean = false) => {
    if (!hasMore && pageNum > 1) return;
    
    setLoading(true);
    try {
      const response = await api.get<ApiResponse>('/api/pitch-decks', {
        params: {
          page: pageNum,
          limit: ITEMS_PER_PAGE
        }
      });
      
      
      if (response.data.pitch_decks) {
        if (append) {
          setAnalyses(prev => [...prev, ...response.data.pitch_decks]);
        } else {
          setAnalyses(response.data.pitch_decks);
        }
        setTotalCount(response.data.total_count);
        setHasMore(response.data.pitch_decks.length === ITEMS_PER_PAGE && 
                  (pageNum * ITEMS_PER_PAGE) < response.data.total_count);
      } else {
        console.error('No pitch_decks in response:', response.data);
        setAnalyses([]);
        setHasMore(false);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching pitch decks:', err);
      setError(err.response?.data?.message || 'Failed to fetch pitch decks');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyses(1, false);
  }, [api]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchAnalyses(nextPage, true);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAnalysisClick = (analysis: Analysis) => {
    if (analysis.analysis_version_id) {
      navigate(`/analysis/${analysis.analysis_version_id}`);
    } else {
      console.error('No analysis version ID found for pitch deck:', analysis);
    }
  };

  if (error) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Something went wrong. Please try again later</h2>
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  // If there are no analyses and not loading, return null (don't render anything)
  if (analyses.length === 0 && !loading) {
    return null;
  }

  return (
    <div className="mt-12">
      <Card className="p-6 relative bg-white/5 dark:bg-zinc-900/5 backdrop-blur-sm">
        <div className="flex flex-col items-center">
          <h2 className="text-2xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
              Previous Analysis
            </span>
          </h2>
        </div>
        
        <div className="space-y-4">
          {analyses.map((analysis) => (
            <div
              key={analysis.analysis_version_id}
              onClick={() => navigate(`/analysis/${analysis.analysis_version_id}`)}
              className="cursor-pointer"
            >
              <Card className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col text-left">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {analysis.company_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {analysis.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(analysis.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-gray-400">
                    <svg 
                      className="h-6 w-6" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
        
        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}
        
        {hasMore && !loading && (
          <div className="text-center py-4">
            <RainbowButton
              onClick={loadMore}
              className="mt-2"
            >
              Load More
            </RainbowButton>
          </div>
        )}
      </Card>
    </div>
  );
};
